/* ============================================================================
   Auth module — "Forgot password?" dialog.
   Opened from the sign-in form. Collects an email and asks the server to send
   a reset link. The API is intentionally non-enumerating (it always returns
   200), so this dialog ALWAYS shows the same neutral confirmation — it never
   reveals whether the address has an account.
   Reuses the admin Modal shell and the shared field/error design classes.
   ============================================================================ */

import { useState } from 'react';
import { apiPost, isApiError } from '../../lib/api';
import Modal from '../admin/Modal';

interface Props {
  onClose: () => void;
}

type Status = 'idle' | 'busy' | 'error' | 'sent';

export default function ForgotPasswordDialog({ onClose }: Props) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  const busy = status === 'busy';
  const sent = status === 'sent';

  async function submit() {
    setError(null);
    if (!email.trim()) {
      setStatus('error');
      setError('Enter the email address for your account.');
      return;
    }
    setStatus('busy');
    try {
      await apiPost('/api/auth/forgot-password', { email: email.trim() });
      setStatus('sent');
    } catch (e) {
      setStatus('error');
      setError(isApiError(e) ? e.message : 'Could not submit the request.');
    }
  }

  // --- Confirmation state (neutral, non-enumerating). ----------------------
  if (sent) {
    return (
      <Modal
        title="Check your email"
        onClose={onClose}
        footer={
          <button className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        }
      >
        <p style={{ marginTop: 0 }}>
          If that email exists, a reset link has been sent.
        </p>
        <p className="muted" style={{ marginBottom: 0 }}>
          The link expires in 30 minutes. Check your inbox — and your spam folder — then follow it to
          choose a new password.
        </p>
      </Modal>
    );
  }

  // --- Form state. ---------------------------------------------------------
  return (
    <Modal
      title="Reset your password"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Sending…' : 'Send reset link'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!busy) void submit();
        }}
      >
        <p className="muted" style={{ marginTop: 0, marginBottom: 16 }}>
          Enter your account email and we'll send you a link to reset your password.
        </p>

        {error && (
          <div className="error-text" role="alert" style={{ marginBottom: 14 }}>
            {error}
          </div>
        )}

        <div className="field" style={{ marginBottom: 4 }}>
          <label htmlFor="fp-email">Email</label>
          <input
            id="fp-email"
            className="in"
            type="email"
            autoComplete="email"
            placeholder="you@uvahealth.org"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
        </div>

        {/* Submit on Enter without showing a second button. */}
        <button type="submit" style={{ display: 'none' }} aria-hidden tabIndex={-1} />
      </form>
    </Modal>
  );
}
